import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Avatar, AvatarFallback } from './ui/avatar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Send, Users, Globe, Briefcase, Handshake, AtSign, Reply, Crown, X, UserPlus } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getInitials = (name) => {
  if (!name) return '?';
  return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
};

const formatTime = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const now = new Date();
  const diff = (now - d) / 1000;
  if (diff < 60) return 'Just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return d.toLocaleDateString();
};

const CommentText = ({ text }) => {
  if (!text) return null;
  const parts = text.split(/(@\w[\w\s]*)/g);
  return (
    <p className="text-sm text-gray-800 whitespace-pre-wrap">
      {parts.map((part, i) =>
        part.startsWith('@') ? (
          <span key={i} className="text-[#0F2E6E] font-medium">{part}</span>
        ) : (
          <span key={i}>{part}</span>
        )
      )}
    </p>
  );
};

// Normalize collaborator to {id, name} regardless of input format
const normalizeCollab = (c) => {
  if (typeof c === 'string') return { id: c };
  return { id: c.user_id || c.id, name: c.name };
};

// Collaborators display — includes the task creator
export const CollaboratorsList = ({ task, employees = [], onUpdate }) => {
  const [adding, setAdding] = useState(false);
  if (!task) return null;

  const collabSet = new Set();
  const collaborators = [];

  // Add the task creator first
  if (task.created_by_id) {
    collabSet.add(task.created_by_id);
    const emp = employees.find(e => e.id === task.created_by_id || e.user_id === task.created_by_id);
    collaborators.push({
      id: task.created_by_id,
      name: emp?.name || task.created_by_name || 'Unknown',
      isCreator: true,
    });
  }

  // Add explicit collaborators
  if (task.collaborators?.length) {
    for (const c of task.collaborators) {
      const norm = normalizeCollab(c);
      if (collabSet.has(norm.id)) continue;
      collabSet.add(norm.id);
      const emp = employees.find(e => e.id === norm.id || e.user_id === norm.id);
      collaborators.push({
        id: norm.id,
        name: emp?.name || norm.name || 'Unknown',
        isCreator: false,
      });
    }
  }

  // Also exclude the assigned-to user from the "add" dropdown
  const assignedId = task.assigned_to || task.assigned_to_id;
  const availableEmployees = employees.filter(e => {
    const eId = e.user_id || e.id;
    return !collabSet.has(eId) && eId !== assignedId;
  });

  const handleAdd = (userId) => {
    if (!onUpdate) return;
    const currentIds = (task.collaborators || []).map(c => normalizeCollab(c).id);
    onUpdate([...currentIds, userId]);
    setAdding(false);
  };

  const handleRemove = (userId) => {
    if (!onUpdate) return;
    const currentIds = (task.collaborators || []).map(c => normalizeCollab(c).id);
    onUpdate(currentIds.filter(id => id !== userId));
  };

  return (
    <div className="border-t pt-3">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4 text-gray-500" />
          <span className="text-xs font-medium text-gray-500">Collaborators ({collaborators.length})</span>
        </div>
        {onUpdate && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 text-xs gap-1"
            onClick={() => setAdding(!adding)}
            data-testid="add-collaborator-btn"
          >
            <UserPlus className="w-3.5 h-3.5" />
            Add
          </Button>
        )}
      </div>
      {adding && onUpdate && availableEmployees.length > 0 && (
        <div className="mb-3">
          <Select onValueChange={handleAdd}>
            <SelectTrigger className="h-8 text-sm" data-testid="collaborator-select">
              <SelectValue placeholder="Select team member..." />
            </SelectTrigger>
            <SelectContent>
              {availableEmployees.map(emp => (
                <SelectItem key={emp.user_id || emp.id} value={emp.user_id || emp.id}>
                  {emp.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
      {adding && onUpdate && availableEmployees.length === 0 && (
        <p className="text-xs text-gray-400 mb-2">No more team members available to add</p>
      )}
      <div className="flex flex-wrap gap-2">
        {collaborators.map((c) => (
          <div key={c.id} className="flex items-center gap-1.5 px-2 py-1 bg-gray-50 rounded-full border text-sm" data-testid={`collaborator-${c.id}`}>
            <Avatar className="h-5 w-5">
              <AvatarFallback className="text-[9px]">{getInitials(c.name)}</AvatarFallback>
            </Avatar>
            <span className="text-gray-700">{c.name}</span>
            {c.isCreator && (
              <span className="text-[10px] text-[#0F2E6E] bg-[#0F2E6E]/10 px-1.5 py-0.5 rounded-full font-medium">Creator</span>
            )}
            {!c.isCreator && onUpdate && (
              <button
                type="button"
                className="ml-1 text-gray-400 hover:text-red-500 transition-colors"
                onClick={() => handleRemove(c.id)}
                data-testid={`remove-collaborator-${c.id}`}
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        ))}
        {collaborators.length === 0 && (
          <p className="text-xs text-gray-400">No collaborators yet</p>
        )}
      </div>
    </div>
  );
};

// Comment input with @mention support
const CommentInput = ({ onSend, mentionableUsers, placeholder, autoFocus, compact }) => {
  const [text, setText] = useState('');
  const [showMentions, setShowMentions] = useState(false);
  const [mentionFilter, setMentionFilter] = useState('');
  const [sending, setSending] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (autoFocus) inputRef.current?.focus();
  }, [autoFocus]);

  const handleInput = (e) => {
    const val = e.target.value;
    setText(val);
    const cursorPos = e.target.selectionStart;
    const before = val.slice(0, cursorPos);
    const atIdx = before.lastIndexOf('@');
    if (atIdx >= 0 && (atIdx === 0 || before[atIdx - 1] === ' ')) {
      setMentionFilter(before.slice(atIdx + 1).toLowerCase());
      setShowMentions(true);
    } else {
      setShowMentions(false);
    }
  };

  const insertMention = (userName) => {
    const cursorPos = inputRef.current?.selectionStart || text.length;
    const before = text.slice(0, cursorPos);
    const atIdx = before.lastIndexOf('@');
    const after = text.slice(cursorPos);
    setText(before.slice(0, atIdx) + `@${userName} ` + after);
    setShowMentions(false);
    inputRef.current?.focus();
  };

  const handleSend = async () => {
    if (!text.trim() || sending) return;
    setSending(true);
    const mentionRegex = /@(\w[\w\s]*)/g;
    const foundMentions = [];
    let match;
    while ((match = mentionRegex.exec(text)) !== null) {
      const mentioned = mentionableUsers.find(u =>
        u.name.toLowerCase().startsWith(match[1].toLowerCase().trim())
      );
      if (mentioned) foundMentions.push(mentioned.id);
    }
    await onSend(text.trim(), [...new Set(foundMentions)]);
    setText('');
    setSending(false);
  };

  const filtered = mentionableUsers.filter(u => u.name.toLowerCase().includes(mentionFilter));

  return (
    <div className="relative">
      <div className="flex gap-2">
        <div className="relative flex-1">
          <input
            ref={inputRef}
            type="text"
            value={text}
            onChange={handleInput}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
            placeholder={placeholder || (mentionableUsers.length > 0 ? "Comment... use @ to mention" : "Add a comment...")}
            className={`w-full px-3 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0F2E6E]/30 focus:border-[#0F2E6E] ${compact ? 'py-1.5 text-xs' : 'py-2'}`}
            data-testid="comment-input"
          />
          {showMentions && filtered.length > 0 && (
            <div className="absolute bottom-full left-0 mb-1 w-full bg-white border rounded-lg shadow-lg z-50 max-h-40 overflow-y-auto" data-testid="mention-dropdown">
              {filtered.map(u => (
                <div key={u.id} onClick={() => insertMention(u.name)} className="flex items-center gap-2 px-3 py-2 hover:bg-[#0F2E6E]/5 cursor-pointer">
                  <Avatar className="h-5 w-5"><AvatarFallback className="text-[9px]">{getInitials(u.name)}</AvatarFallback></Avatar>
                  <span className="text-sm">{u.name}</span>
                </div>
              ))}
            </div>
          )}
        </div>
        <Button type="button" size="icon" onClick={handleSend} disabled={!text.trim() || sending}
          className={`bg-[#0F2E6E] hover:bg-[#153A82] flex-shrink-0 ${compact ? 'h-8 w-8' : 'h-9 w-9'}`}
          data-testid="send-comment-button">
          <Send className={compact ? 'h-3.5 w-3.5' : 'h-4 w-4'} />
        </Button>
      </div>
    </div>
  );
};

// Single comment with reply thread
const CommentItem = ({ comment, replies, mentionableUsers, onReply, depth = 0 }) => {
  const [showReplyInput, setShowReplyInput] = useState(false);

  return (
    <div className={depth > 0 ? 'ml-6 pl-3 border-l-2 border-[#0F2E6E]/15' : ''}>
      <div className="flex gap-2" data-testid={`comment-${comment.id}`}>
        <Avatar className="h-7 w-7 flex-shrink-0">
          <AvatarFallback className="text-[10px] bg-[#0F2E6E]/10 text-[#0F2E6E]">{getInitials(comment.user_name)}</AvatarFallback>
        </Avatar>
        <div className="flex-1 bg-gray-50 rounded-lg px-3 py-2">
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-gray-700">{comment.user_name}</span>
            <span className="text-[10px] text-gray-400">{formatTime(comment.created_at)}</span>
          </div>
          <CommentText text={comment.text} />
          <button
            onClick={() => setShowReplyInput(!showReplyInput)}
            className="flex items-center gap-1 mt-1 text-[11px] text-gray-400 hover:text-[#0F2E6E] transition-colors"
            data-testid={`reply-btn-${comment.id}`}
          >
            <Reply className="h-3 w-3" />
            Reply
          </button>
        </div>
      </div>
      {showReplyInput && (
        <div className="ml-9 mt-2 mb-1">
          <CommentInput
            onSend={async (text, mentions) => {
              await onReply(text, mentions, comment.id);
              setShowReplyInput(false);
            }}
            mentionableUsers={mentionableUsers}
            placeholder={`Reply to ${comment.user_name}...`}
            autoFocus
            compact
          />
        </div>
      )}
      {/* Nested replies */}
      {replies.length > 0 && (
        <div className="mt-2 space-y-2">
          {replies.map(r => (
            <CommentItem key={r.id} comment={r} replies={[]} mentionableUsers={mentionableUsers} onReply={onReply} depth={depth + 1} />
          ))}
        </div>
      )}
    </div>
  );
};

// Main comments section
const TaskComments = ({ taskId, collaborators = [], employees = [], task }) => {
  const { getAuthHeaders, user, currentOffice } = useAuth();
  const [comments, setComments] = useState([]);

  // Build mentionable users: collaborators + creator (deduped, excluding self)
  const mentionableUsers = (() => {
    const seen = new Set();
    const users = [];
    // Add creator
    if (task?.created_by_id && task.created_by_id !== user?.id) {
      seen.add(task.created_by_id);
      const emp = employees.find(e => e.id === task.created_by_id || e.user_id === task.created_by_id);
      users.push({ id: task.created_by_id, name: emp?.name || task.created_by_name || 'Unknown' });
    }
    // Add collaborators
    for (const c of collaborators) {
      const norm = normalizeCollab(c);
      if (seen.has(norm.id) || norm.id === user?.id) continue;
      seen.add(norm.id);
      const emp = employees.find(e => e.id === norm.id || e.user_id === norm.id);
      users.push({ id: norm.id, name: emp?.name || norm.name || 'Unknown' });
    }
    return users;
  })();

  const fetchComments = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/tasks/${taskId}/comments`, { headers: getAuthHeaders() });
      if (res.ok) setComments(await res.json());
    } catch (e) {
      console.error('Failed to load comments', e);
    }
  }, [taskId, getAuthHeaders]);

  useEffect(() => {
    if (taskId) fetchComments();
  }, [taskId, fetchComments]);

  const postComment = async (text, mentions, parentId = null) => {
    try {
      const headers = getAuthHeaders();
      headers['Content-Type'] = 'application/json';
      if (currentOffice?.id) headers['X-Office-ID'] = currentOffice.id;
      const res = await fetch(`${API_URL}/api/tasks/${taskId}/comments`, {
        method: 'POST', headers,
        body: JSON.stringify({ text, mentions, parent_id: parentId }),
      });
      if (res.ok) {
        const newComment = await res.json();
        setComments(prev => [...prev, newComment]);
        if (mentions.length > 0) toast.success('Comment posted & collaborators notified');
      }
    } catch (e) {
      toast.error('Failed to post comment');
    }
  };

  // Separate top-level comments and replies
  const topLevel = comments.filter(c => !c.parent_id);
  const repliesMap = {};
  for (const c of comments) {
    if (c.parent_id) {
      if (!repliesMap[c.parent_id]) repliesMap[c.parent_id] = [];
      repliesMap[c.parent_id].push(c);
    }
  }

  return (
    <div className="border-t pt-3" data-testid="task-comments-section">
      <div className="flex items-center gap-2 mb-3">
        <AtSign className="w-4 h-4 text-gray-500" />
        <span className="text-xs font-medium text-gray-500">Comments ({comments.length})</span>
      </div>

      {comments.length > 0 && (
        <div className="space-y-3 mb-3 max-h-64 overflow-y-auto pr-1">
          {topLevel.map(c => (
            <CommentItem
              key={c.id}
              comment={c}
              replies={repliesMap[c.id] || []}
              mentionableUsers={mentionableUsers}
              onReply={postComment}
            />
          ))}
        </div>
      )}

      <CommentInput onSend={(text, mentions) => postComment(text, mentions)} mentionableUsers={mentionableUsers} />
    </div>
  );
};

export default TaskComments;
